// src/components/VaccineReservationList.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Heading,
  VStack,
  HStack,
  Spinner,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faSearch } from "@fortawesome/free-solid-svg-icons";
import DataTable from "./AppDataTable";
import { tableCustomStyles, StatusBadge } from "../themes/TableStyles";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import * as IconButton from "../themes/IconButtonSet";

// Custom styles for DataTable
const customStyles = {
  header: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "18px",
      fontWeight: "bold",
    },
  },
  headCells: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "16px",
      fontWeight: "semibold",
      padding: "10px",
    },
  },
  cells: {
    style: {
      padding: "10px",
      fontSize: "14px",
    },
  },
  rows: {
    style: {
      "&:hover": {
        backgroundColor: "#f5f5f5",
      },
    },
  },
};

// ฟังก์ชันกำหนดสไตล์สถานะ
const getStatusStyle = (status) => {
  switch (status) {
    case "ใบจองใหม่":
      return { bg: "#E6FFFA", color: "#065F46" };
    case "รอการอนุมัติ":
      return { bg: "#FFF3E0", color: "#D97706" };
    case "ยืนยันการจอง":
      return { bg: "#D1FAE5", color: "#047857" };
    case "พร้อมรับวัคซีน":
      return { bg: "#DBEAFE", color: "#1E40AF" };
    case "รับวัคซีนแล้ว":
      return { bg: "#E5E7EB", color: "#374151" };
    case ("ยกเลิกการจอง" || "Cancelled"):
      return { bg: "#FEE2E2", color: "#991B1B" };
    case "ปฏิเสธการจอง":
      return { bg: "#FFEDD5", color: "#EA580C" };
    case "เกินกำหนดรับ":
      return { bg: "#EDE9FE", color: "#6B7280" };
    default:
      return { bg: "#EDF2F7", color: "#4A5568" }; // Default ถ้าสถานะไม่ตรง
  }
};

const OfficeRsvList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [filteredReservations, setFilteredReservations] = useState([]);
  const [pickupPoints, setPickupPoints] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // ตรวจสอบการล็อกอิน
  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const columns = [
    {
      name: "เลขที่ใบจอง",
      selector: (row) => row.ReservationID,
      sortable: true,
    },
    { name: "ชื่อเกษตรกร", selector: (row) => row.FarmerName, sortable: true },
    {
      name: "รอบการจัดส่ง",
      selector: (row) => row.DeliveryPeriod || "-",
      sortable: true,
    },
    {
      name: "จุดจำหน่าย",
      selector: (row) => {
        const point = pickupPoints.find((p) => p.WarehouseID === row.PickupPoint);
        return point ? point.WarehouseName : row.PickupPoint || "-";
      },
      sortable: true,
    },
    {
      name: "วันที่จอง",
      selector: (row) => new Date(row.ReservationDate).toLocaleString(),
      sortable: true,
    },
    {
      name: "สถานะ",
      selector: (row) => <StatusBadge status={row.ReservationStatus} />,
      sortable: true,
      width: "150px",
    },
    {
      name: "การดำเนินการ",
      cell: (row) => (
        <IconButton.View
          icon={faFileLines}
          onClick={() => navigate(`/officersv/${row.ReservationID}`)}
          label="ดูรายละเอียด"
          width="100px"
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "120px",
    },
  ];

  const fetchPickupPoints = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinePickupPointList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setPickupPoints(response.VaccineWarehousesList || []);
      }
    } catch (error) {
      console.error("Error fetching pickup points:", error);
    }
  };

  const fetchReservations = async () => {
    setIsLoading(true);
    try {
      await fetchPickupPoints();
      const response = await apiService({
        method: "POST",
        url: "/getVaccineOrdersList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const data = response.VaccineReservationsList || [];
        setReservations(data);
        setFilteredReservations(data);
      }
    } catch (error) {
      console.error("Error fetching reservations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReservations();
  }, []);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);

    if (!value) {
      setFilteredReservations(reservations);
      return;
    }

    const searchTerms = value.split(" ").filter((term) => term.length > 0);
    const filtered = reservations.filter((row) => {
      const pickupPointName =
        pickupPoints.find((p) => p.WarehouseID === row.PickupPoint)?.WarehouseName ||
        row.PickupPoint ||
        "";

      // ตรวจสอบว่าทุกคำค้นหาตรงกับฟิลด์ใดฟิลด์หนึ่ง
      return searchTerms.every((term) =>
        [
          row.ReservationID.toLowerCase(),
          row.FarmerName.toLowerCase(),
          row.DeliveryPeriod ? row.DeliveryPeriod.toLowerCase() : "",
          pickupPointName.toLowerCase(),
          row.ReservationStatus.toLowerCase(),
        ].some((field) => field.includes(term))
      );
    });
    setFilteredReservations(filtered);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilteredReservations(reservations);
  };

  const handleCreateNew = () => {
    navigate("/officersv/new");
  };

  return (
    <Box
      p={{ base: 4, md: 10, lg: 35 }}
      m={{ base: 2, md: 10, lg: 25 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={6} align="start">
        <Box
          mb={5}
          borderRadius="full"
          w="100%"
          bgColor="iBlue.600"
          color="white"
          p={2}
        >
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faFileLines} color="white" size="xl" />
            <Heading
              as="h1"
              size={{ base: "md", md: "lg" }}
              fontWeight="normal"
            >
              รายการใบจองวัคซีน
            </Heading>
          </HStack>
        </Box>
        <HStack
          w="100%"
          justify="space-between"
          flexWrap={{ base: "wrap", md: "nowrap" }}
        >
          {reservations.length > 0 && (
            <InputGroup
              w={{ base: "100%", md: "50%", lg: "30%" }}
              mb={{ base: 4, md: 0 }}
            >
              <InputLeftElement pointerEvents="none">
                <FontAwesomeIcon icon={faSearch} color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="ค้นหาใบจอง..."
                value={searchTerm}
                onChange={handleSearch}
                colorScheme="iBlue"
                mt={1}
              />
              <IconButton.Clear onClick={handleClearSearch} ml={5} />
            </InputGroup>
          )}
          <IconButton.Add onClick={handleCreateNew} label="สร้างใบจองใหม่" />
        </HStack>
        {isLoading ? (
          <Spinner size="md" />
        ) : reservations.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ยังไม่มีข้อมูลใบจองวัคซีน
          </Text>
        ) : filteredReservations.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ไม่พบข้อมูลที่ค้นหา
          </Text>
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={filteredReservations}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default OfficeRsvList;